/**
 * The demo customer's world: one invented customer ("Alex", a dental practice) with a handful of campaigns,
 * in the exact response shapes our dashboard reads (mapped 2026-09-24 against services/db.ts and the
 * Dot-Matrix pages). Deterministic and relative to today, so the demo never looks stale. Every address uses
 * the reserved .example domain.
 */

export const USER_ID = 'demo-user-0001';

const DAY = 86400000;
const now = new Date();
const iso = (d: Date) => d.toISOString().slice(0, 19) + 'Z';
export const dayStr = (offset: number) => new Date(now.getTime() + offset * DAY).toISOString().slice(0, 10);

/** Small deterministic noise so charts look alive but render the same every time. */
function wobble(seed: number, i: number, spread = 0.06) {
  const x = Math.sin(seed * 97.13 + i * 12.9898) * 43758.5453;
  return 1 + (x - Math.floor(x) - 0.5) * 2 * spread;
}

interface Camp { id: string; name: string; url: string; tier: 'professional' | 'expert'; daily: number; status: string; target: number; ageDays: number; left: number; countries: string[]; seed: number; reason?: string }

export const CAMPAIGNS: Camp[] = [
  { id: 'demo-p1', name: 'Homepage', url: 'https://www.bella-dental.example/', tier: 'professional', daily: 3200, status: 'active', target: 0, ageDays: 41, left: 49, countries: ['US'], seed: 1 },
  { id: 'demo-p2', name: 'Implants landing page', url: 'https://www.bella-dental.example/implants', tier: 'expert', daily: 1200, status: 'active', target: 60000, ageDays: 22, left: 30, countries: ['US', 'CA'], seed: 2 },
  { id: 'demo-p3', name: 'Blog · whitening guide', url: 'https://www.bella-dental.example/blog/whitening', tier: 'professional', daily: 900, status: 'active', target: 30000, ageDays: 18, left: 2, countries: ['US', 'GB'], seed: 3 },
  { id: 'demo-p4', name: 'Summer promo', url: 'https://www.bella-dental.example/summer', tier: 'professional', daily: 600, status: 'paused', target: 20000, ageDays: 64, left: 12, countries: ['US'], seed: 4 },
  { id: 'demo-p5', name: 'Spring check-up campaign', url: 'https://www.bella-dental.example/check-up', tier: 'professional', daily: 1000, status: 'completed', target: 30000, ageDays: 95, left: -20, countries: ['US'], seed: 5 },
];

const hourShare = [0.018, 0.014, 0.012, 0.011, 0.012, 0.016, 0.026, 0.038, 0.049, 0.057, 0.061, 0.062, 0.06, 0.059, 0.06, 0.061, 0.06, 0.058, 0.055, 0.052, 0.047, 0.04, 0.032, 0.024];
const currentHour = now.getUTCHours();
const shareByNow = hourShare.slice(0, currentHour).reduce((a, b) => a + b, 0) + hourShare[currentHour] * (now.getUTCMinutes() / 60);

function series(c: Camp, days: number) {
  if (c.status === 'completed') return [];
  return Array.from({ length: days }, (_, i) => {
    const offset = i - days;
    if (-offset > c.ageDays) return null;
    const paused = c.status === 'paused' && offset > -12;
    const visitors = paused ? 0 : Math.round(c.daily * wobble(c.seed, i));
    return { date: dayStr(offset), visitors, pageviews: Math.round(visitors * 2.6 * wobble(c.seed + 9, i, 0.04)) };
  }).filter(Boolean) as Array<{ date: string; visitors: number; pageviews: number }>;
}

const hitsToday = (c: Camp) => (c.status === 'active' ? Math.round(c.daily * shareByNow) : 0);

export function rawProject(c: Camp) {
  const totalHits = c.status === 'completed' ? c.target : series(c, Math.min(c.ageDays, 90)).reduce((s, p) => s + p.visitors, 0);
  return {
    id: c.id, user_id: USER_ID, name: c.name, status: c.status, plan_type: c.tier === 'expert' ? 'Expert' : 'Professional', tier: c.tier,
    settings: {
      entryUrls: c.url, trafficSource: 'organic', timezone: 'UTC', schedulePattern: 'realistic',
      trafficMix: [{ category: 'organic', percent: 60 }, { category: 'direct', percent: 25 }, { category: 'social', percent: 15 }],
      geoTargets: c.countries.map((cc, i) => ({ id: `g${i}`, country: cc === 'US' ? 'United States' : cc === 'CA' ? 'Canada' : 'United Kingdom', countryCode: cc, percent: Math.round(100 / c.countries.length) })),
      countries: c.countries, languages: ['en'], language: 'en', deviceSplit: 55, tabletSplit: 5, browser: 'Random', deviceSpecific: 'All',
      bounceRate: 35, returnRate: 20, timeOnPage: '1 - 3 minutes', gaId: 'G-DEMO000000', innerUrls: '', exitUrls: '',
      autoCrawlEntry: false, autoCrawlInner: true, autoCrawlExit: false, innerUrlCount: 3,
    },
    daily_limit: c.daily, total_target: c.target, hits_today: hitsToday(c), total_hits: totalHits, settings_version: 1, deduct_credits: true,
    start_at: iso(new Date(now.getTime() - c.ageDays * DAY)), created_at: iso(new Date(now.getTime() - c.ageDays * DAY)),
    expires_at: iso(new Date(now.getTime() + c.left * DAY)), duration_days: c.ageDays + c.left, priority: 0,
    force_stop_reason: c.reason ?? null, is_hidden: false, internal_tags: [], notes: null, is_flagged: false,
  };
}

export const projects = () => CAMPAIGNS.map(rawProject);
export const projectStats = (days: number) => Object.fromEntries(CAMPAIGNS.map((c) => [c.id, series(c, days)]));
export const projectSeries = (id: string, days: number) => { const c = CAMPAIGNS.find((x) => x.id === id); return c ? series(c, days) : []; };
export const findCampaign = (id: string) => CAMPAIGNS.find((c) => c.id === id);

const active = () => CAMPAIGNS.filter((c) => c.status === 'active');
const planTotal = () => active().reduce((s, c) => s + c.daily, 0);

export const BALANCES = { economy: 0, professional: 184000, expert: 42000, seo: 64 };

export function overviewToday() {
  const plan = planTotal();
  const hours = hourShare.map((share, hour) => {
    const planned = Math.round(plan * share);
    const delivered = hour < currentHour ? Math.round(planned * wobble(7, hour, 0.05)) : hour === currentHour ? Math.round(planned * (now.getUTCMinutes() / 60)) : 0;
    return { hour, delivered, planned, last_week: Math.round(planned * wobble(8, hour, 0.12) * 0.92) };
  });
  const delivered = hours.reduce((s, h) => s + h.delivered, 0);
  const proDaily = active().filter((c) => c.tier === 'professional').reduce((s, c) => s + c.daily, 0);
  const expDaily = active().filter((c) => c.tier === 'expert').reduce((s, c) => s + c.daily, 0);
  return {
    timezone: 'UTC', now: iso(now), current_hour: currentHour, hours,
    today: { delivered, last_week_same_time: Math.round(delivered * 0.9), plan_total: plan, planned_by_now: Math.round(plan * shareByNow) },
    campaigns: { running: active().length, paused: CAMPAIGNS.filter((c) => c.status === 'paused').length, total: CAMPAIGNS.length, ending_within_3_days: CAMPAIGNS.filter((c) => c.status === 'active' && c.left <= 3).length },
    runway: [
      { wallet: 'professional', balance: BALANCES.professional, daily_plan: proDaily, days: +(BALANCES.professional / proDaily).toFixed(1), campaigns: 2, basis: 'plan_today' },
      { wallet: 'expert', balance: BALANCES.expert, daily_plan: expDaily, days: +(BALANCES.expert / expDaily).toFixed(1), campaigns: 1, basis: 'plan_today' },
      { wallet: 'seo', balance: BALANCES.seo, daily_plan: 0, days: null, campaigns: 2, basis: 'used_30d' },
    ],
  };
}

export const dashboardInit = () => ({
  gamification: { level: 4, level_name: 'Starter', xp: 620, xp_to_next: 380, total_spent: 468, discount_percent: 1, pending_bonus_hits: 0, pending_bonus_claimed: true, streak_days: 6, streak_best: 14, next_reward: '+5,000 visits, 2% discount' },
  streak: { streak_days: 6, streak_best: 14, can_claim_today: true, claim_unavailable_reason: null, next_bonus: 1500, legacy_streak_rate: false },
  active_now: 41,
});

const login = [3, 2, 1, 0].map((d) => ({ date: dayStr(-d), ip: '203.0.113.10', device: 'Chrome on macOS', type: 'password' }));

/** camelCase user as the dashboard keeps it in localStorage. */
export const DEMO_USER = {
  id: USER_ID, email: 'alex@bella-dental.example', name: 'Alex', displayName: 'Alex Morgan', role: 'user', status: 'active',
  isVerified: true, isIdentityVerified: true, joinedDate: dayStr(-120), createdAt: iso(new Date(now.getTime() - 120 * DAY)),
  balance: BALANCES.professional, balanceEconomy: BALANCES.economy, balanceProfessional: BALANCES.professional, balanceExpert: BALANCES.expert, balanceSeo: BALANCES.seo,
  projectsCount: CAMPAIGNS.length, loginHistory: login, streakDays: 6, streakBest: 14, gamificationLevel: 4, gamificationXp: 620,
  gamificationTotalSpent: 468, gamificationPermanentDiscount: 1, company: 'Bella Dental', website: 'https://www.bella-dental.example',
};

/** snake_case user as GET /users/me returns it. */
export const meResponse = () => ({
  id: USER_ID, email: DEMO_USER.email, name: 'Alex', display_name: 'Alex Morgan', role: 'user', status: 'active',
  is_verified: true, is_identity_verified: true, balance: BALANCES.professional, balance_economy: BALANCES.economy,
  balance_professional: BALANCES.professional, balance_expert: BALANCES.expert, balance_seo: BALANCES.seo,
  created_at: DEMO_USER.createdAt, projects_count: CAMPAIGNS.length, login_history: login, phone: '', job_title: 'Practice manager',
  website: 'https://www.bella-dental.example', bio: '', company: 'Bella Dental', vat_id: '', address: '12 Harbor Street', city: 'Portland',
  zip: '97201', country: 'United States', avatar_url: null, streak_days: 6, streak_best: 14, gamification_level: 4, gamification_xp: 620,
  gamification_total_spent: 468, gamification_permanent_discount: 1, fasterpay_subscription_id: null, affiliate_code: 'ALEX15',
});
